import { createAnimationLegacy } from '../common/schemas/legacy/animation';
import { createExtendedPositionLegacy } from '../common/schemas/legacy/extended-position';
import { createWidgetPositionLegacy } from '../common/schemas/legacy/widget-position';

import type { ButtonEntity, ButtonItem, LegacyButton, LegacyButtonDefault, LegacyButtonZero } from './schemas';
import { createButtonItem } from './schemas';

/**
 * Which legacy placement the factory should produce — corresponds to the
 * envelope's `type` discriminator.
 *  - `'default'` (or omitted): a button dropped into a normal column. The
 *    persisted envelope omits the `type` field entirely for byte-identical
 *    parity with pre-discriminator data on disk. Data carries the simple
 *    per-device position grid and `isSynchronizeStyle: true`.
 *  - `'zero'`: a button dropped into a zero block. Envelope carries
 *    `type: 'zero'`; data uses the extended-position grid, the single seeded
 *    button defaults to `widthType: 'fixed'`, and `isSynchronizeStyle` starts
 *    `false` (zero-block widgets are always per-device).
 */
export type LegacyButtonVariant = 'default' | 'zero';

/**
 * Per-variant creation knobs. The host app passes `buttonOverrides` to seed
 * the first button with localised text (`label`), a generated `elementId`
 * (cuid2) and the default icon SVG — values this package deliberately
 * doesn't own (no i18n, no id-generator, no icon-template dependencies).
 */
export type CreateLegacyButtonOptions = {
    buttonOverrides?: Partial<ButtonItem>;
};

const createDefaultData = (options?: CreateLegacyButtonOptions): LegacyButtonDefault['data'] => ({
    buttons: [createButtonItem(options?.buttonOverrides)],
    horizontalIndent: 'M',
    horizontalAlign: 'L',
    mobileHorizontalAlign: 'L',
    tabletHorizontalAlign: 'L',
    isSynchronizeStyle: true,
    hideOnDesktop: false,
    hideOnTablet: false,
    hideOnMobile: false,
    position: createWidgetPositionLegacy(),
    animation: createAnimationLegacy(),
});

/**
 * Default zero-block dimensions for a single button — mirrors the values the
 * host-app form factory passes into `webWidgetExtendedPositionSettingsFormGroup`
 * for the zero button variant (82×51 on every device).
 */
const createZeroData = (options?: CreateLegacyButtonOptions): LegacyButtonZero['data'] => ({
    buttons: [createButtonItem({ widthType: 'fixed', ...options?.buttonOverrides })],
    horizontalIndent: 'M',
    horizontalAlign: 'L',
    mobileHorizontalAlign: 'L',
    tabletHorizontalAlign: 'L',
    isSynchronizeStyle: false,
    hideOnDesktop: false,
    hideOnTablet: false,
    hideOnMobile: false,
    position: createExtendedPositionLegacy({
        width: 82,
        tabletWidth: 82,
        mobileWidth: 82,
        height: 51,
        tabletHeight: 51,
        mobileHeight: 51,
    }),
    animation: createAnimationLegacy(),
});

/**
 * Factory for the current legacy button entity — matches the persisted widget
 * JSON shape: `{ id, name: 'button', columnId, data, … }`. Variant selects
 * between default-column placement (no `type` field, simple position grid)
 * and zero-block placement (`type: 'zero'`, extended-position grid).
 *
 * When the redesigned canonical `default/v0` lands, add a sibling
 * `createDefaultButton` returning that shape; the registry will dispatch
 * between them on a discriminator field.
 */
export function createLegacyButton(variant?: 'default', options?: CreateLegacyButtonOptions): LegacyButtonDefault;
export function createLegacyButton(variant: 'zero', options?: CreateLegacyButtonOptions): LegacyButtonZero;
export function createLegacyButton(
    variant: LegacyButtonVariant = 'default',
    options?: CreateLegacyButtonOptions
): LegacyButton {
    if (variant === 'zero') {
        return {
            id: '',
            name: 'button',
            columnId: '',
            type: 'zero',
            data: createZeroData(options),
        };
    }

    return {
        id: '',
        name: 'button',
        columnId: '',
        data: createDefaultData(options),
    };
}

/**
 * Convenience entry-point that returns whichever shape is the current default
 * for new button widgets. While only `legacy` exists, this delegates to
 * {@link createLegacyButton}; pass the variant to opt into the zero-block tag.
 */
export function createButton(variant?: 'default', options?: CreateLegacyButtonOptions): LegacyButtonDefault;
export function createButton(variant: 'zero', options?: CreateLegacyButtonOptions): LegacyButtonZero;
export function createButton(variant?: LegacyButtonVariant, options?: CreateLegacyButtonOptions): ButtonEntity {
    // `as any` because the overload signatures resolve at the call site; the
    // implementation just forwards the variant.
    return createLegacyButton(variant as any, options);
}
